import { useQuery } from '@tanstack/react-query';  
import { supabase } from '@/integrations/supabase/client';
import { ProgressTracking } from '@/types/progress';
import { useEffect, useCallback } from 'react';

export interface EnhancedProgressState {
  progress?: ProgressTracking | null;
  processed: number;
  total: number;
  status: 'idle' | 'pending' | 'processing' | 'completed' | 'error';
  stage?: string;
  source: 'progress_tracking' | 'product_count' | 'none';
  isCompleted: boolean;
  isProcessing: boolean;
  percentage: number;
  action: string;
  lastUpdated: Date;
  processType: 'product_upload' | 'translation' | 'import' | 'none';
}

export const useEnhancedProgressState = (collectionId: string) => {
  // Clean up stale translation records
  const cleanupStaleTranslationRecords = useCallback(async () => {
    try {
      const { data: products } = await supabase
        .from('product_descriptions')
        .select('id, processing_status')
        .eq('collection_id', collectionId);

      const totalProducts = products?.length || 0;
      const processedProducts = products?.filter(p => 
        p.processing_status === 'completed' || p.processing_status === 'error'
      ).length || 0;

      // If all products are completed, clean up any stale translation records
      if (totalProducts > 0 && processedProducts >= totalProducts) {
        const { data: staleRecords } = await supabase
          .from('progress_tracking')
          .select('id')
          .eq('collection_id', collectionId)
          .eq('process_type', 'translation')
          .eq('status', 'processing')
          .eq('processed_items', 0)
          .eq('total_items', 0);

        if (staleRecords && staleRecords.length > 0) {
          console.log('[useEnhancedProgressState] Cleaning up stale translation records:', staleRecords.length);
          await supabase
            .from('progress_tracking')
            .delete()
            .in('id', staleRecords.map(r => r.id));
        }
      }
    } catch (error) {
      console.error('[useEnhancedProgressState] Error cleaning up stale records:', error);
    }
  }, [collectionId]);

  // Sync progress tracking with actual product counts when data is inconsistent
  const syncProgressTracking = useCallback(async () => {
    try {
      const { data: products } = await supabase
        .from('product_descriptions')
        .select('id, processing_status')
        .eq('collection_id', collectionId);

      const totalProducts = products?.length || 0;
      const processedProducts = products?.filter(p => p.processing_status === 'completed' || p.processing_status === 'error').length || 0;
      
      if (totalProducts > 0) {
        const isCompleted = processedProducts >= totalProducts;
        const progressId = `upload-${collectionId}`;
        
        await supabase
          .from('progress_tracking')
          .upsert({
            id: progressId,
            collection_id: collectionId,
            status: isCompleted ? 'completed' : 'processing',
            processed_items: processedProducts,
            total_items: totalProducts,
            current_stage: isCompleted ? 'completed' : 'processing',
            current_action: isCompleted ? 'AI generation completed' : `Generating AI descriptions ${processedProducts}/${totalProducts}`,
            process_type: 'product_upload',
            notification_sent: false,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'id'
          });
        
        console.log('[useEnhancedProgressState] Synced progress tracking:', {
          processed: processedProducts,
          total: totalProducts,
          status: isCompleted ? 'completed' : 'processing'
        });
      }
    } catch (error) {
      console.error('[useEnhancedProgressState] Error syncing progress:', error);
    }
  }, [collectionId]);

  const query = useQuery({
    queryKey: ['enhanced-progress-state', collectionId],
    queryFn: async (): Promise<EnhancedProgressState> => {
      console.log('[useEnhancedProgressState] Fetching enhanced progress state for:', collectionId);
      
      // Try to get progress tracking data for both upload and translation
      const { data: progressRows, error: progressError } = await supabase
        .from('progress_tracking')
        .select('*')
        .eq('collection_id', collectionId)
        .in('process_type', ['product_upload', 'translation', 'import'])
        .order('updated_at', { ascending: false });

      if (progressError && progressError.code !== 'PGRST116') {
        console.error('Error fetching progress tracking:', progressError);
      }

      // Choose the most relevant row with improved logic to avoid stale translation records
      let progressData: any = null;
      let processType: 'product_upload' | 'translation' | 'import' | 'none' = 'none';
      if (progressRows && progressRows.length > 0) {
        // Get actual product counts first for validation
        const { data: products } = await supabase
          .from('product_descriptions')
          .select('id, processing_status')
          .eq('collection_id', collectionId);
        
        const totalProducts = products?.length || 0;
        const processedProducts = products?.filter(p => 
          p.processing_status === 'completed' || p.processing_status === 'error'
        ).length || 0;
        
        // Filter out stale translation records more aggressively
        const validRows = progressRows.filter(row => {
          if (row.process_type === 'translation' && row.status === 'processing') {
            // Stale translation conditions:
            // 1. 0 progress with completed products
            // 2. Very old record (>30 minutes) with no progress
            // 3. Total items mismatch with actual products when products exist
            const isStaleTranslation = 
              ((row.processed_items === 0 && row.total_items === 0) && totalProducts > 0 && processedProducts >= totalProducts) ||
              ((row.processed_items === 0 && row.total_items === 0) && totalProducts > 0) ||
              (row.total_items !== totalProducts && totalProducts > 0 && row.processed_items === 0) ||
              (new Date().getTime() - new Date(row.updated_at).getTime() > 30 * 60 * 1000 && row.processed_items === 0);
            
            if (isStaleTranslation) {
              console.log('[useEnhancedProgressState] Detected stale translation record, ignoring:', {
                id: row.id,
                processed: row.processed_items,
                total: row.total_items,
                actualProducts: totalProducts,
                completedProducts: processedProducts,
                age: new Date().getTime() - new Date(row.updated_at).getTime()
              });
              return false;
            }
          }
          return true;
        });
        
        if (validRows.length > 0) {
          // Strongly prioritize upload/import records for original collections
          const uploadCompleted = validRows.find(r => (r.process_type === 'product_upload' || r.process_type === 'import') && r.status === 'completed');
          const uploadProcessing = validRows.find(r => (r.process_type === 'product_upload' || r.process_type === 'import') && r.status === 'processing');
          const translationProcessing = validRows.find(r => r.process_type === 'translation' && r.status === 'processing' && r.total_items > 0);
          
          // Priority order for original collections:
          // 1. Completed upload/import (shows final state)
          // 2. Processing upload/import (current generation)  
          // 3. Processing translation (only if has meaningful progress)
          // 4. Any other record
          let pick;
          if (uploadCompleted || uploadProcessing) {
            pick = uploadCompleted || uploadProcessing;
            console.log('[useEnhancedProgressState] Prioritizing upload/import record for original collection');
          } else if (translationProcessing && translationProcessing.total_items > 0 && translationProcessing.processed_items > 0) {
            pick = translationProcessing;
            console.log('[useEnhancedProgressState] Using translation record with meaningful progress');
          } else {
            pick = validRows[0];
            console.log('[useEnhancedProgressState] Fallback to first valid record');
          }
          
          progressData = pick;
          processType = (pick?.process_type as any) || 'none';
        }
      }

      // Get actual product counts
      const { data: products, error: productsError } = await supabase
        .from('product_descriptions')
        .select('id, status, processing_status, source, ai_generated')
        .eq('collection_id', collectionId);

      if (productsError) {
        console.error('Error fetching products:', productsError);
        throw productsError;
      }

      const totalProducts = products?.length || 0;
      // Only count 'completed' as processed (not errors or pending)
      const processedProducts = products?.filter(p => 
        p.processing_status === 'completed'
      ).length || 0;

      // ENHANCED: Check if progress tracking data is outdated or inconsistent (only for product upload)
      const isProgressTrackingOutdated = progressData && progressData.process_type === 'product_upload' && totalProducts > 0 && (
        progressData.total_items === 0 || 
        (progressData.total_items !== totalProducts && Math.abs(progressData.total_items - totalProducts) > 1) ||
        (progressData.status === 'processing' && processedProducts >= totalProducts) ||
        // CRITICAL: Mark as outdated if showing processing but all products are actually completed
        (progressData.status === 'processing' && processedProducts === totalProducts && totalProducts > 0) ||
        // CRITICAL: Fix stuck zero progress - if tracking shows 0/0 but products exist and are processing
        (progressData.processed_items === 0 && progressData.total_items === 0 && totalProducts > 0)
      );

      // Auto-sync if progress tracking is outdated
      if (isProgressTrackingOutdated) {
        console.log('[useEnhancedProgressState] Detected outdated progress tracking, syncing...');
        // Clean up stale translation records first
        await cleanupStaleTranslationRecords();
        // Immediately sync progress tracking to prevent stale data display
        await syncProgressTracking();
        
        // Force return product-based state for immediate display while sync happens
        const isCompleted = processedProducts >= totalProducts;
        return {
          progress: progressData,
          processed: processedProducts,
          total: totalProducts,
          status: isCompleted ? 'completed' : 'processing',
          stage: isCompleted ? 'completed' : 'processing',
          source: 'product_count',
          isCompleted,
          isProcessing: !isCompleted,
          percentage: totalProducts > 0 ? (processedProducts / totalProducts) * 100 : 0,
          action: isCompleted ? 'AI generation completed' : `Generating AI descriptions ${processedProducts}/${totalProducts}`,
          lastUpdated: new Date(),
          processType: 'product_upload'
        };
      }

      // Determine the most accurate state
      let finalState: EnhancedProgressState;

      if (progressData && (!isProgressTrackingOutdated || processType === 'translation')) {
        // We have accurate progress tracking data (always trust translation row; for upload ensure not outdated)
        const isCompleted = progressData.status === 'completed' || 
                           (progressData.total_items > 0 && progressData.processed_items >= progressData.total_items);
        
        finalState = {
          progress: progressData,
          processed: progressData.processed_items,
          total: progressData.total_items,
          status: progressData.status as any,
          stage: progressData.current_stage,
          source: 'progress_tracking',
          isCompleted,
          isProcessing: progressData.status === 'processing' && !isCompleted,
          percentage: progressData.total_items > 0 ? (progressData.processed_items / progressData.total_items) * 100 : 0,
          action: progressData.current_action || (processType === 'translation' ? 'Translating...' : processType === 'import' ? 'Importing...' : 'Processing...'),
          lastUpdated: new Date(progressData.updated_at || progressData.created_at),
          processType
        };
      } else if (totalProducts > 0) {
        // Use product counts as source of truth (either no progress tracking or it's outdated)
        const isCompleted = processedProducts >= totalProducts;
        
        // Determine operation type based on product data
        const hasImportedProducts = products?.some(p => p.source === 'excel') || false;
        const hasAIGeneratedProducts = products?.some(p => p.ai_generated === true) || false;
        
        let operationType: 'import' | 'product_upload' = 'product_upload';
        let actionText = '';
        
        if (hasImportedProducts && !hasAIGeneratedProducts) {
          operationType = 'import';
          actionText = isCompleted ? 'Import completed' : `Importing products ${processedProducts}/${totalProducts}`;
        } else {
          operationType = 'product_upload';
          actionText = isCompleted ? 'AI generation completed' : `Generating AI descriptions ${processedProducts}/${totalProducts}`;
        }

        // Force completion when all products are processed
        const isActuallyCompleted = processedProducts >= totalProducts && totalProducts > 0;
        
        finalState = {
          progress: progressData,
          processed: processedProducts,
          total: totalProducts,
          status: isActuallyCompleted ? 'completed' : 'processing',
          stage: isActuallyCompleted ? 'completed' : 'processing',
          source: 'product_count',
          isCompleted: isActuallyCompleted,
          isProcessing: !isActuallyCompleted,
          percentage: totalProducts > 0 ? Math.min(100, (processedProducts / totalProducts) * 100) : 0,
          action: isActuallyCompleted ? (operationType === 'import' ? 'Import completed' : 'AI generation completed') : actionText,
          lastUpdated: new Date(),
          processType: operationType
        };
        
        console.log('[useEnhancedProgressState] Using product counts as source of truth:', {
          reason: isProgressTrackingOutdated ? 'outdated_progress' : 'no_progress',
          progressData: progressData ? { 
            total: progressData.total_items, 
            processed: progressData.processed_items,
            status: progressData.status,
            stage: progressData.current_stage
          } : null,
          actualCounts: { processed: processedProducts, total: totalProducts }
        });
      } else {
        // No progress tracking and no products - this is an empty collection
        finalState = {
          progress: null,
          processed: 0,
          total: 0,
          status: 'idle',
          source: 'none',
          isCompleted: false,
          isProcessing: false,
          percentage: 0,
          action: 'Ready',
          stage: 'idle',
          lastUpdated: new Date(),
          processType: 'none'
        };
      }

      console.log('[useEnhancedProgressState] Enhanced state calculated:', finalState);
      return finalState;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.isCompleted) return false;
      return data.status === 'processing' ? 1000 : false; // Faster polling for better responsiveness
    },
    staleTime: 500, // Reduced stale time for better real-time feel
    gcTime: 30000
  });

  // Set up real-time subscription for progress tracking
  useEffect(() => {
    const channel = supabase
      .channel(`progress-tracking-${collectionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'progress_tracking',
          filter: `collection_id=eq.${collectionId}`
        },
        (payload) => {
          console.log('[useEnhancedProgressState] Real-time progress update:', payload);
          query.refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_descriptions',
          filter: `collection_id=eq.${collectionId}`
        },
        (payload) => {
          console.log('[useEnhancedProgressState] Real-time product update:', payload);
          query.refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [collectionId, query]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    syncProgressTracking,
    cleanupStaleTranslationRecords
  };
};